import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { db } from '../db';
import electronBridge from '../utils/electronBridge';

// MENU_VERSION: Bump this number when you change ALL_MENU_ITEMS (add, remove, rename, etc.)
// This will trigger a merge that preserves user's custom order while applying your changes
const MENU_VERSION = 8;

// Define the default menu items
const ALL_MENU_ITEMS = [
  { id: 'playground', label: 'Playground', to: '/', icon: 'GenerateIcon', activeIcon: 'GenerateIconActive' },
  { id: 'comfy', label: 'Comfy Output', to: '/comfy', icon: 'GenerateIcon', activeIcon: 'GenerateIconActive', conditionalRender: 'comfyVisibility' },
  { id: 'collection', label: 'My Collection', to: '/my-collection', icon: 'MyCollectionIcon', activeIcon: 'MyCollectionIconActive' },
  { id: 'templates', label: 'CosyTemplates', to: '/cosyflows', icon: 'TemplatesIcon', activeIcon: 'TemplatesIconActive' },
  // { id: 'cosyflow-editor', label: 'Cosyflow Editor', to: '/cosyflow-editor', icon: 'TemplatesIcon', activeIcon: 'TemplatesIconActive' },
  { id: 'profile', label: 'Profile', to: '/profile', icon: 'ProfileIcon', activeIcon: 'ProfileIconActive', dynamicLabel: true },
  { id: 'explore', label: 'Explore', to: '/explore', icon: 'ExploreIcon', activeIcon: 'ExploreIconActive' },
  { id: 'momm', label: 'MOMM', to: '/momm', icon: 'MOMMIcon', activeIcon: 'MOMMIconActive' },
  { id: 'settings', label: 'Settings', to: '/settings', icon: 'SettingsIcon', activeIcon: 'SettingsIconActive' },
  { id: 'help', label: 'Help', to: '/how-to', icon: 'HowToIcon', activeIcon: 'HowToIconActive' },
  { id: 'chat', label: 'Chat', to: '/chat', icon: 'ChatIcon', activeIcon: 'ChatIconActive' },
  { id: 'comfyui', label: 'Comfy UI', to: '/comfyui', icon: 'ComfyUIIcon', activeIcon: 'ComfyUIIconActive' },
  { id: 'cosyui', label: 'Cosy UI', to: '/cosyui', icon: 'ComfyUIIcon', activeIcon: 'ComfyUIIconActive' },
];

// Filter out ComfyOutput and ComfyUI if not in Electron environment (but keep MOMM)
const DEFAULT_MENU_ITEMS = electronBridge.isElectron
  ? ALL_MENU_ITEMS
  : ALL_MENU_ITEMS.filter(item => item.id !== 'comfy');

const MENU_CACHE_KEY = 'promptus_menu_order_cache';

const getInitialMenuItems = () => {
  try {
    const cached = localStorage.getItem(MENU_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      return electronBridge.isElectron
        ? parsed
        : parsed.filter(item => item.id !== 'comfy');
    }
  } catch (e) {
    console.error('[MenuContext] Error reading from localStorage cache:', e);
  }
  return DEFAULT_MENU_ITEMS;
};

const saveToCache = (menuItems) => {
  try {
    localStorage.setItem(MENU_CACHE_KEY, JSON.stringify(menuItems));
  } catch (e) {
    console.error('[MenuContext] Error saving to localStorage cache:', e);
  }
};

const MenuStateContext = createContext();
const MenuDispatchContext = createContext();

const menuReducer = (state, action) => {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        ...state,
        menuItems: action.payload,
        isEditMode: true
      };
    case 'REORDER':
      return {
        ...state,
        menuItems: action.payload,
        isDirty: true
      };
    case 'TOGGLE_EDIT_MODE':
      return {
        ...state,
        isEditMode: !state.isEditMode
      };
    case 'SAVE_ORDER':
      return {
        ...state,
        isEditMode: true,
        isDirty: false
      };
    case 'SAVE_WITHOUT_EXIT':
      return {
        ...state,
        isDirty: false
      };
    case 'RESET_ORDER':
      return {
        ...state,
        menuItems: DEFAULT_MENU_ITEMS,
        isEditMode: true,
        isDirty: true
      };
    default:
      return state;
  }
};

export const MenuProvider = ({ children }) => {
  const [state, dispatch] = useReducer(menuReducer, {
    menuItems: getInitialMenuItems(),
    isEditMode: true,
    isDirty: false
  });

  // Load menu order from DB on first render
  useEffect(() => {
    const loadMenuOrder = async () => {
      try {
        const setting = await db.settings.where('key').equals('menu_order').first();
        const versionSetting = await db.settings.where('key').equals('menu_version').first();
        const storedVersion = versionSetting?.value;

        if (setting && setting.value) {
          // Filter out ComfyOutput in browser environment
          const storedMenuItems = electronBridge.isElectron
            ? setting.value
            : setting.value.filter(item => item.id !== 'comfy');

          // If no version exists or version mismatch, merge changes while preserving order
          if (storedVersion === undefined || storedVersion !== MENU_VERSION) {
            console.log(`[MenuContext] Menu version changed (${storedVersion} -> ${MENU_VERSION}), merging changes...`);

            const defaultsMap = new Map(DEFAULT_MENU_ITEMS.map(item => [item.id, item]));

            // Keep user's order, but update properties and filter removed items
            const mergedItems = storedMenuItems
              .filter(item => defaultsMap.has(item.id))
              .map(item => ({ ...defaultsMap.get(item.id) }));

            // Append any new items at the end
            const existingIds = new Set(mergedItems.map(item => item.id));
            const newItems = DEFAULT_MENU_ITEMS.filter(item => !existingIds.has(item.id));

            if (newItems.length > 0) {
              console.log(`[MenuContext] Adding ${newItems.length} new menu items:`, newItems.map(item => item.label));
            }

            const finalItems = [...mergedItems, ...newItems];

            await db.settings.put({ key: 'menu_order', value: finalItems });
            await db.settings.put({ key: 'menu_version', value: MENU_VERSION });
            saveToCache(finalItems);
            dispatch({ type: 'INITIALIZE', payload: finalItems });
          } else {
            // Version matches, use stored order as-is
            saveToCache(storedMenuItems);
            dispatch({ type: 'INITIALIZE', payload: storedMenuItems });
          }
        } else {
          // Initialize with default order if setting doesn't exist
          console.log('[MenuContext] No menu order found in database, initializing with defaults');
          await db.settings.put({ key: 'menu_order', value: DEFAULT_MENU_ITEMS });
          await db.settings.put({ key: 'menu_version', value: MENU_VERSION });
          saveToCache(DEFAULT_MENU_ITEMS);
          dispatch({ type: 'INITIALIZE', payload: DEFAULT_MENU_ITEMS });
        }
      } catch (error) {
        console.error('Error loading menu order:', error);
        // Fallback to default menu if there's an error
        dispatch({ type: 'INITIALIZE', payload: DEFAULT_MENU_ITEMS });
      }
    };

    loadMenuOrder();
  }, []);

  // Save menu order when it changes
  useEffect(() => {
    if (state.isDirty) {
      const saveMenuOrder = async () => {
        try {
          await db.settings.put({ key: 'menu_order', value: state.menuItems });
          saveToCache(state.menuItems);
        } catch (error) {
          console.error('Error saving menu order:', error);
        }
      };

      saveMenuOrder();
    }
  }, [state.isDirty, state.menuItems]);

  return (
    <MenuStateContext.Provider value={state}>
      <MenuDispatchContext.Provider value={dispatch}>
        {children}
      </MenuDispatchContext.Provider>
    </MenuStateContext.Provider>
  );
};

export const useMenuState = () => {
  const context = useContext(MenuStateContext);
  if (context === undefined) {
    throw new Error('useMenuState must be used within a MenuProvider');
  }
  return context;
};

export const useMenuDispatch = () => {
  const context = useContext(MenuDispatchContext);
  if (context === undefined) {
    throw new Error('useMenuDispatch must be used within a MenuProvider');
  }
  return context;
};

export const getDefaultMenuItems = () => DEFAULT_MENU_ITEMS; 